import { FetchResult, useApolloClient } from "@apollo/client";

import { MessagingStrategyName } from "~frontend/__generated__/graphql";
import { EDIT_CAMPAIGN_MESSAGING_STRATEGY } from "~frontend/graphql/campaigns";
import { runMutationAndNormalizeErrors } from "~frontend/util/useMutation";

import Bandwidth10DLCMessagingStrategyConfig from "./Bandwidth10DLC";
import Bandwidth10DLCPoolMessagingStrategyConfig from "./Bandwidth10DLCPool";
import BandwidthShortCodeMessagingStrategyConfig from "./BandwidthShortCode";
import BandwidthTollFreeMessagingStrategyConfig from "./BandwidthTollFree";
import BandwidthUnregisteredPoolMessagingStrategyConfig from "./BandwidthUnregisteredPool";
import FakeMessagingStrategyConfig from "./Fake";
import Fake10DLCMessagingStrategyConfig from "./Fake10DLC";
import FakePoolMessagingStrategyConfig from "./FakePool";
import FakeShortCodeMessagingStrategyConfig from "./FakeShortCode";
import FakeTollFreeMessagingStrategyConfig from "./FakeTollFree";
import Sinch10DLCMessagingStrategyConfig from "./Sinch10DLC";
import TwilioShortCodeMessagingStrategyConfig from "./TwilioShortCode";
import { CampaignMessagingStrategy } from "./types";

export function useMessagingStrategyMutation(): <
  T extends MessagingStrategyName,
>({
  campaignId,
  strategy,
}: {
  campaignId: string;
  strategy: Omit<CampaignMessagingStrategy<T>, "__typename">;
}) => Promise<FetchResult<CampaignMessagingStrategy<T>>> {
  const apollo = useApolloClient();

  return async ({ campaignId, strategy }) => {
    const mutation = EDIT_CAMPAIGN_MESSAGING_STRATEGY[strategy.strategy];

    const { errors, data } = await runMutationAndNormalizeErrors(
      () =>
        apollo.mutate({
          mutation,
          variables: {
            input: {
              campaignId,
              config:
                "config" in strategy && strategy.config
                  ? strategy.config
                  : undefined,
            },
          },
        }),
      []
    );

    if (errors) {
      return { errors, data: null };
    }

    const mutationName = Object.keys(data ?? {}).find(k =>
      k.startsWith("editCampaignMessagingStrategy")
    );
    return {
      errors: undefined,
      data:
        data && mutationName
          ? (data as Record<string, any>)[mutationName]?.messagingStrategy
          : null,
    };
  };
}

export const messagingStrategyComponents = {
  FAKE: FakeMessagingStrategyConfig,
  BANDWIDTH_10_DLC: Bandwidth10DLCMessagingStrategyConfig,
  BANDWIDTH_10_DLC_POOL: Bandwidth10DLCPoolMessagingStrategyConfig,
  BANDWIDTH_TOLL_FREE: BandwidthTollFreeMessagingStrategyConfig,
  BANDWIDTH_SHORT_CODE: BandwidthShortCodeMessagingStrategyConfig,
  BANDWIDTH_UNREGISTERED_POOL: BandwidthUnregisteredPoolMessagingStrategyConfig,
  TWILIO_SHORT_CODE: TwilioShortCodeMessagingStrategyConfig,
  SINCH_10_DLC: Sinch10DLCMessagingStrategyConfig,
  FAKE_POOL: FakePoolMessagingStrategyConfig,
  FAKE_SHORT_CODE: FakeShortCodeMessagingStrategyConfig,
  FAKE_10_DLC: Fake10DLCMessagingStrategyConfig,
  FAKE_TOLL_FREE: FakeTollFreeMessagingStrategyConfig,
};
